import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";

/**
 * Hook that wires up the browser's Web Push API to our backend.
 *
 * Exposes:
 *   - supported: boolean — does this browser/runtime support Push at all?
 *   - permission: "default" | "granted" | "denied"
 *   - subscribed: boolean — do we already have a live subscription?
 *   - isStandalone: boolean — running as installed PWA?
 *   - isIos: boolean — running on iPhone/iPad?
 *   - enable(): request permission, subscribe, store on backend.
 *   - disable(): unsubscribe locally + on backend.
 *
 * iOS quirk: Safari only allows Push from a PWA installed to the Home
 * Screen (iOS 16.4+). In a regular tab it reports `pushManager` undefined
 * and we return `supported: false` with a hint.
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export default function usePushNotifications({ mode = "customer" } = {}) {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  // mode "astrologer" → stores subscription under astrologer_id via the
  // /api/astrologer/push endpoints. Used by the astrologer dashboard so
  // incoming chat/call rings reach them as a phone push notification.
  const subscribeUrl = mode === "astrologer" ? "/astrologer/push/subscribe" : "/push/subscribe";
  const unsubscribeUrl = mode === "astrologer" ? "/astrologer/push/unsubscribe" : "/push/unsubscribe";

  const isIos = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true);

  // Detect feature support + sync current subscription state on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (typeof window === "undefined") return;
      // Inside the Capacitor Android app, Web Push must be OFF — the app
      // subscribes via FCM in `capacitorPush.js` and Web Push would
      // cause a duplicate notification whose tap opens the URL in
      // Chrome/Samsung Internet (service-worker `openWindow` can't
      // re-focus the Capacitor WebView, so the browser is the only
      // handler that fires). Report `supported: false` here so no
      // "Enable notifications" UI ever prompts for it in-app.
      const isCapacitorNative = !!(
        window.Capacitor &&
        window.Capacitor.isNativePlatform &&
        window.Capacitor.isNativePlatform()
      );
      if (isCapacitorNative) {
        if (!cancelled) setSupported(false);
        return;
      }
      const featureOk =
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        typeof Notification !== "undefined";
      if (!featureOk) {
        if (!cancelled) setSupported(false);
        return;
      }
      try {
        // Ensure the SW is registered & active before we ask for a subscription.
        let reg = await navigator.serviceWorker.getRegistration("/");
        if (!reg) {
          reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
        }
        await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();
        if (!cancelled) {
          setSupported(true);
          setSubscribed(!!existing);
          setPermission(Notification.permission);
        }
      } catch (e) {
        if (!cancelled) setSupported(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const enable = useCallback(async () => {
    if (busy) return { ok: false, error: "busy" };
    setBusy(true);
    try {
      // Ask permission (must be inside the user-gesture handler — caller's onClick).
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") return { ok: false, error: "permission_denied" };

      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });

      const subJson = sub.toJSON();
      await api.post(subscribeUrl, {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        expirationTime: subJson.expirationTime || null,
      });
      setSubscribed(true);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message || "unknown" };
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const disable = useCallback(async () => {
    if (busy) return { ok: false };
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        try { await api.post(unsubscribeUrl, { endpoint }); } catch (e) { /* ignore */ }
      }
      setSubscribed(false);
      return { ok: true };
    } catch (e) {
      return { ok: false };
    } finally {
      setBusy(false);
    }
  }, [busy]);

  return { supported, permission, subscribed, busy, isIos, isStandalone, enable, disable };
}
